import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 no-underline">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category ? category.name : article.category;
  const relatedArticles = getRelatedArticles(article.id, article.category);

  const getCategoryName = (slug: string) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  const pageTitle = `${article.title} - 91大事件`;
  const pageDesc = article.summary;
  const pageKeywords = `${article.tags.join(',')},91大事件,历史事件,${categoryName}`;
  const canonicalUrl = `https://www.91dashijian.com/article/${article.id}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '91大事件',
    },
    publisher: {
      '@type': 'Organization',
      name: '91大事件',
    },
    image: article.imageUrl,
    url: canonicalUrl,
    articleSection: categoryName,
    keywords: article.tags.join(','),
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${article.title}发生在什么时候？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${article.title}发生在${article.date}。`,
        },
      },
      {
        '@type': 'Question',
        name: `${article.title}属于哪个历史分类？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${article.title}属于${categoryName}领域的重大历史事件。`,
        },
      },
      {
        '@type': 'Question',
        name: `${article.title}有什么历史意义？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${article.summary}`,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <SEOMeta
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.date}
      />
      <Navbar />
      <main className="pt-16">
        <article className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: categoryName, path: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-500 text-background-50 text-xs rounded-full whitespace-nowrap no-underline"
                >
                  {categoryName}
                </Link>
                <span className="text-sm text-foreground-500">{article.date}</span>
                <span className="text-sm text-foreground-500">{article.readTime} 分钟阅读</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                {article.summary}
              </p>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <div
              className="prose prose-foreground max-w-none mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard
                    key={rel.id}
                    id={rel.id}
                    title={rel.title}
                    summary={rel.summary}
                    category={rel.category}
                    categoryName={getCategoryName(rel.category)}
                    date={rel.date}
                    readTime={rel.readTime}
                    imageUrl={rel.imageUrl}
                    tags={rel.tags}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}